import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal.tsx";
import { Trans } from "react-i18next";
import { Button } from "@/components/ui/button.tsx";
import { useNavigate } from "@tanstack/react-router";
import { PaymentType } from "../payment/utils";
import { useRemovePayment } from "@/hooks/use-remove-payment";
import { useShop } from "@/hooks/use-shop";
import { getDriverUrl } from "@/lib/runtime-config";
import { useMutation } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { AudioSlot, useAudioSlot } from "@/features/audio/kiosk-audio";

export const NotEnoughChangeModal = ({ onClose }: { onClose: () => void }) => {
  return (
    <Modal>
      <ModalTitle>
        <Trans>Figyelem!</Trans>
      </ModalTitle>
      <ModalContent>
        <p className={`leading-6 mb-12`}>
          <Trans>Az automatában nem volt elég visszajáró.</Trans>
        </p>
        <Button onClick={onClose}>
          <Trans>Ok</Trans>
        </Button>
      </ModalContent>
    </Modal>
  );
};

export const TimedOutModal = ({
  payment,
  onDone,
}: {
  payment: PaymentType;
  /** After the payment was removed: back to the payment method choice. */
  onDone: () => void;
}) => {
  useAudioSlot("modal.cash-payment.timed-out");
  const { closeModal } = useMuiModal();
  const removePayment = useRemovePayment();
  const handleClick = async () => {
    closeModal();
    try {
      await removePayment.mutateAsync({
        key: payment.kioskPaymentNonceKey,
        cause: "cash-payment-timeout",
      });
    } catch (e) {
      console.error(e);
    }
    onDone();
  };

  return (
    <Modal>
      <ModalTitle>
        <Trans>Fizetés időtúllépés!</Trans>
      </ModalTitle>
      <ModalContent>
        <p className={`leading-6 mb-12`}>
          <Trans>
            A fizetési folyamat túllépte az időkorlátot. A rendszer a bedobott
            összeget érmében visszaadja. Kérjük, próbálja meg a fizetést
            újrakezdeni!
          </Trans>
        </p>
        <Button onClick={handleClick}>
          <Trans>Ok</Trans>
        </Button>
      </ModalContent>
    </Modal>
  );
};

export const AbortedModal = ({
  payment,
  onDone,
}: {
  payment: PaymentType;
  /** After the payment was removed: back to the payment method choice. */
  onDone: () => void;
}) => {
  useAudioSlot("modal.cash-payment.aborted");
  const { closeModal } = useMuiModal();
  const removePayment = useRemovePayment();
  const handleClick = async () => {
    closeModal();
    removePayment.mutateAsync({
      key: payment.kioskPaymentNonceKey,
      cause: "cancelled-by-user",
    });
    onDone();
  };
  return (
    <Modal>
      <ModalTitle>
        <Trans>Fizetés megszakadt</Trans>
      </ModalTitle>
      <ModalContent>
        <p className={`leading-6 mb-12`}>
          <Trans>Ön a fizetést megszakította.</Trans>
        </p>
        <Button onClick={handleClick}>
          <Trans>Ok</Trans>
        </Button>
      </ModalContent>
    </Modal>
  );
};

export const HardwareErrorModal = ({
  payment,
  onDone,
}: {
  payment: PaymentType;
  /** After the payment was removed: back to the payment method choice. */
  onDone: () => void;
}) => {
  useAudioSlot("modal.cash-payment.hardware-error");
  const { closeModal } = useMuiModal();
  const removePayment = useRemovePayment();
  const handleClick = async () => {
    closeModal();
    try {
      await removePayment.mutateAsync({
        key: payment.kioskPaymentNonceKey,
        cause: "error",
        info: "hardware-error",
      });
    } catch (e) {
      console.error(e);
    }
    onDone();
  };
  return (
    <Modal>
      <ModalTitle>
        <Trans>Hardver hiba!</Trans>
      </ModalTitle>
      <ModalContent>
        <p className={`leading-6 mb-12`}>
          <Trans>
            A fizetés közben műszaki probléma történt. A bedobott pénzt a gép
            érmékben visszaadja.
          </Trans>
        </p>
        <Button onClick={handleClick}>
          <Trans>Ok</Trans>
        </Button>
      </ModalContent>
    </Modal>
  );
};

export const ServerErrorModal = ({
  payment,
  onDone,
}: {
  payment: PaymentType;
  /** After the payment was removed: back to the payment method choice. */
  onDone: () => void;
}) => {
  useAudioSlot("modal.cash-payment.server-error");
  const { closeModal } = useMuiModal();
  const removePayment = useRemovePayment();
  const handleClick = async () => {
    closeModal();
    try {
      await removePayment.mutateAsync({
        key: payment.kioskPaymentNonceKey,
        cause: "error",
        info: "unknown-error",
      });
    } catch (e) {
      console.error(e);
    }
    onDone();
  };
  return (
    <Modal>
      <ModalTitle>
        <Trans>Szerver hiba!</Trans>
      </ModalTitle>
      <ModalContent>
        <p className={`leading-6 mb-12`}>
          <Trans>
            A fizetés során szerver hiba lépett fel. A rendszer a bedobott
            összeget érmében visszaadja. Kérjük, próbálja meg a fizetést
            újrakezdeni!
          </Trans>
        </p>
        <Button onClick={handleClick}>
          <Trans>Ok</Trans>
        </Button>
      </ModalContent>
    </Modal>
  );
};

export const PrinterErrorModal = ({
  transactionId,
}: {
  transactionId?: string;
}) => {
  useAudioSlot("modal.printer-error");
  const { closeModal } = useMuiModal();
  const shop = useShop();
  const email = shop.data.email;
  return (
    <Modal>
      <ModalTitle>
        <Trans>NYOMTATÓ HIBA</Trans>
      </ModalTitle>
      <ModalContent>
        <p className="leading-6 mb-6 text-center">
          <Trans>
            A nyomtató meghibásodása miatt nem sikerült a nyugta nyomtatása.
            Kérjük amennyiben szüksége lenne a nyugtára fotózza le az alábbi
            azonosítót és küldje el a {{ email }} címre a fotót.
          </Trans>
        </p>
        {transactionId && (
          <p className="font-bold text-center mb-8">
            <Trans>Tranzakció azonosító szám</Trans>: {transactionId}
          </p>
        )}
        <Button onClick={() => closeModal()}>
          <Trans>Ok</Trans>
        </Button>
      </ModalContent>
    </Modal>
  );
};

export const CashPaymentMessageModal = ({
  title,
  children,
  onClose,
}: {
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
}) => {
  return (
    <Modal>
      <ModalTitle>{title}</ModalTitle>
      <ModalContent>
        <div className="mb-12 leading-6">{children}</div>
        <Button onClick={onClose}>
          <Trans>Ok</Trans>
        </Button>
      </ModalContent>
    </Modal>
  );
};

/** Balance top-up ended but the change could not be paid out. */
export const BalanceTopupPayoutFailedModal = ({
  aborted,
  supportPhone,
  onClose,
}: {
  aborted: boolean;
  supportPhone: string;
  onClose: () => void;
}) => (
  <CashPaymentMessageModal
    title={<Trans>Visszajáró hiba</Trans>}
    onClose={onClose}
  >
    {aborted ? (
      <>
        <AudioSlot slot="modal.balance-topup.aborted-payout-failed" />
        <Trans values={{ supportPhone }}>
          A feltöltés megszakadt, de a visszajáró kiadása nem sikerült.
          Kérjük, hívd az ügyfélszolgálatot a {{ supportPhone }}{" "}
          telefonszámon!
        </Trans>
      </>
    ) : (
      <>
        <AudioSlot slot="modal.balance-topup.success-payout-failed" />
        <Trans values={{ supportPhone }}>
          A feltöltés sikeres volt, de a visszajáró kiadása nem sikerült.
          Kérjük, hívd az ügyfélszolgálatot a {{ supportPhone }}{" "}
          telefonszámon!
        </Trans>
      </>
    )}
  </CashPaymentMessageModal>
);

export const ConnectionLostModal = ({
  shopId,
  paymentJobId,
  orderId,
  payment,
}: {
  shopId: number;
  paymentJobId: number;
  orderId: string;
  payment: PaymentType;
}) => {
  const navigate = useNavigate();
  const removePayment = useRemovePayment();
  const [showAbortOption, setShowAbortOption] = useState(false);

  const supportCode = `${shopId}-${paymentJobId}-${orderId.slice(0, 3)}`;

  useEffect(() => {
    const timer = setTimeout(() => setShowAbortOption(true), 10_000);
    return () => clearTimeout(timer);
  }, []);

  const navigateToPaymentSelector = () => {
    navigate({
      to: "/payment-selector-screen/$machineId/{-$dryerBundleId}",
      params: {
        machineId: payment.machineId,
        dryerBundleId: payment.dryerBundleId,
      },
    });
  };

  const driverAbort = useMutation({
    mutationFn: async () => {
      const driverUrl = getDriverUrl();
      const response = await fetch(`${driverUrl}/cashpayment/disable/false`, {
        method: "POST",
      });
      if (!response.ok) {
        throw new Error("Driver responded with error");
      }
      const result = await response.json();
      if (!result.Success) {
        throw new Error("Driver abort unsuccessful");
      }
      return result;
    },
  });

  const handleGoBack = async () => {
    try {
      await removePayment.mutateAsync({
        key: payment.kioskPaymentNonceKey,
        cause: "error",
        info: "connection-lost-abort-success",
      });
    } catch (e) {
      console.error(e);
    }
    navigateToPaymentSelector();
  };

  const handleForfeitGoBack = async () => {
    try {
      await removePayment.mutateAsync({
        key: payment.kioskPaymentNonceKey,
        cause: "error",
        info: "connection-lost-forfeited",
      });
    } catch (e) {
      console.error(e);
    }
    navigateToPaymentSelector();
  };

  return (
    <Modal>
      <ModalTitle>
        <Trans>Kapcsolat megszakadt!</Trans>
      </ModalTitle>
      <ModalContent>
        {driverAbort.isIdle && (
          <>
            <p className="leading-6 mb-4">
              <Trans>A kapcsolat megszakadt.</Trans>
            </p>
            <p className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
              <Loader2 className="animate-spin size-4" />
              <Trans>Újracsatlakozás...</Trans>
            </p>
            {showAbortOption && (
              <>
                <p className="leading-6 mb-8">
                  <Trans>
                    Ha nem szeretne tovább várni, megszakíthatja a fizetést. A
                    bedobott összeget a gép érmékben visszaadja.
                  </Trans>
                </p>
                <Button onClick={() => driverAbort.mutate()}>
                  <Trans>Fizetés megszakítása</Trans>
                </Button>
              </>
            )}
          </>
        )}

        {driverAbort.isPending && (
          <>
            <p className="leading-6 mb-8">
              <Trans>Fizetés megszakítása folyamatban...</Trans>
            </p>
            <Loader2 className="animate-spin size-8" />
          </>
        )}

        {driverAbort.isSuccess && (
          <>
            <p className="leading-6 mb-8">
              <Trans>
                A fizetés sikeresen megszakítva. A bedobott összeget a gép
                érmékben visszaadja.
              </Trans>
            </p>
            <Button onClick={handleGoBack}>
              <Trans>Vissza a fizetés választáshoz</Trans>
            </Button>
          </>
        )}

        {driverAbort.isError && (
          <>
            <p className="leading-6 mb-8">
              <Trans>
                A fizetés megszakítása nem sikerült. Fényképezze le az alábbi
                kódot, és hívja az ügyfélszolgálatot a probléma megoldásához.
              </Trans>
            </p>
            <p className="font-bold text-3xl mb-8 font-mono">{supportCode}</p>
            <Button onClick={handleForfeitGoBack}>
              <Trans>Vissza a fizetés választáshoz</Trans>
            </Button>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
